import React, { useState, useCallback } from 'react';
import {
  Box,
  Button,
  Flex,
  useColorModeValue,
  useDisclosure,
  Drawer,
  DrawerBody,
  DrawerHeader,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  useBreakpointValue,
  IconButton,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Tooltip,
} from '@chakra-ui/react';
import { FiPlus, FiMenu, FiUpload } from 'react-icons/fi';
import JournalEntryList from './components/JournalEntryList';
import JournalEditor from './components/JournalEditor';
import SearchBar from './components/SearchBar';
import SearchResults from './components/SearchResults';
import { useSearch } from '../../contexts/SearchContext';
import { useJournal } from '../../contexts/JournalContext';
import JournalImport from './components/JournalImport';
import { JournalEntry, JournalEntryData } from '../../types/journal';
import { useSubscription } from 'contexts/SubscriptionContext';

const JournalPage: React.FC = () => {
  const [selectedEntry, setSelectedEntry] = useState<JournalEntry | null>(null);
  const [isCreatingNew, setIsCreatingNew] = useState(false);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { searchQuery, searchResults } = useSearch();
  const isMobile = useBreakpointValue({ base: true, md: false });
  const bgColor = useColorModeValue('gray.50', 'gray.900');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const sidebarBgColor = useColorModeValue('white', 'gray.800');
  const [importModalOpen, setImportModalOpen] = useState(false);
  const { entries, isLoading, createEntry, updateEntry } = useJournal();
  const { subscription } = useSubscription();

  const handleImportSuccess = useCallback(() => {
    setTimeout(() => setImportModalOpen(false), 2000);
  }, []);

  const handleNewEntry = () => {
    setSelectedEntry(null);
    setIsCreatingNew(true);
    if (isMobile) {
      onClose();
    }
  };

  const handleEntrySelect = (entry: JournalEntry | null) => {
    setSelectedEntry(entry);
    setIsCreatingNew(false);
    if (isMobile) {
      onClose();
    }
  };

  const handleSave = async (entryData: JournalEntryData) => {
    if (selectedEntry) {
      await updateEntry(
        selectedEntry.id,
        entryData.content,
        entryData.mood_label,
        entryData.entry_date,
        entryData.location,
        entryData.weather,
        entryData.tags,
        entryData.custom_fields
      );
    } else {
      const { entry: newEntry } = await createEntry(
        entryData.content,
        entryData.mood_label,
        entryData.entry_date,
        entryData.location,
        entryData.weather,
        entryData.tags,
        entryData.custom_fields
      );
      setSelectedEntry(newEntry);
      setIsCreatingNew(false);
    }
  };

  const sidebarContent = (
    <Box height="100%" display="flex" flexDirection="column">
      <Box p={4}>
        <Flex gap={2} mb={4}>
          <Button
            leftIcon={<FiPlus />}
            colorScheme="blue"
            flex="1"
            onClick={handleNewEntry}
          >
            New Entry
          </Button>
          {subscription?.isSubscribed ? (
            <Button
              leftIcon={<FiUpload />}
              variant="outline"
              onClick={() => setImportModalOpen(true)}
            >
              Import
            </Button>
          ) : (
            <Tooltip label="Upgrade to Premium to import journal entries">
              <Button
                leftIcon={<FiUpload />}
                variant="outline"
                isDisabled
              >
                Import
              </Button>
            </Tooltip>
          )}
        </Flex>
        <SearchBar />
      </Box>
      <Box flex="1" overflowY="auto" px={4} pb={4}>
        {searchQuery || searchResults.length > 0 ? (
          <SearchResults />
        ) : (
          <JournalEntryList 
            entries={entries} 
            selectedEntry={selectedEntry} 
            onSelectEntry={handleEntrySelect} 
            isLoading={isLoading} 
          />
        )}
      </Box>
    </Box>
  );

  return (
    <Flex height="100%" bg={bgColor}>
      {/* Mobile Menu Button */}
      {isMobile && (
        <IconButton
          aria-label="Open menu"
          icon={<FiMenu />}
          position="fixed"
          top={4}
          left={4}
          zIndex={2}
          onClick={onOpen}
        />
      )}

      {/* Sidebar */}
      {isMobile ? (
        <Drawer isOpen={isOpen} placement="left" onClose={onClose}>
          <DrawerOverlay>
            <DrawerContent>
              <DrawerCloseButton />
              <DrawerHeader>Journal Entries</DrawerHeader>
              <DrawerBody p={0}>{sidebarContent}</DrawerBody>
            </DrawerContent>
          </DrawerOverlay>
        </Drawer>
      ) : (
        <Box
          width="350px"
          height="100%"
          borderRight="1px solid"
          borderColor={borderColor}
          bg={sidebarBgColor}
        >
          {sidebarContent}
        </Box>
      )}

      {/* Main Content */}
      <Box flex="1" p={4} height="100%" overflowY="auto">
        <JournalEditor
          entry={selectedEntry}
          onSave={handleSave}
        />
      </Box>

      {subscription?.isSubscribed && (
        <Modal
          isOpen={importModalOpen}
          onClose={() => setImportModalOpen(false)}
          size="xl"
        >
          <ModalOverlay />
          <ModalContent maxW="600px">
            <ModalHeader>Import Journal Entries</ModalHeader>
            <ModalCloseButton />
            <ModalBody pb={6}>
              <JournalImport onSuccess={handleImportSuccess} />
            </ModalBody>
          </ModalContent>
        </Modal>
      )}
    </Flex>
  );
};

export default JournalPage; 